import { Injectable, inject } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { Subject, distinctUntilChanged, filter, map, tap } from 'rxjs';
import { Category } from 'src/app/models/category.interface';
import { Icon } from 'src/app/models/icon.interface';
import { IconStyle } from 'src/app/models/iconstyle.interface';
import { Size } from 'src/app/models/iconSize.interface';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class IconsService {
    // Common
    public error = new Subject<boolean>();
    public errorMsg = '';
    // Version information
    public vloading = new Subject<boolean>();
    public version = 'Not loaded';
    public vloaded = new CustomEvent('iconsloaded');
    // Icons
    public loading = new Subject<boolean>();
    public icons = new Array<Icon>();
    public loaded = new CustomEvent('iconsloaded');
    // Icon Styles
    public sloading = new Subject<boolean>();
    public iconstyles = new Array<IconStyle>();
    public sloaded = new CustomEvent('styleloaded');
    // Categories
    public cloading = new Subject<boolean>();
    public categories = new Array<Category>();
    public cloaded = new CustomEvent('categoryloaded');
    // Sizes
    public zloading = new Subject<boolean>();
    public sizes = new Array<Size>();
    public zloaded = new CustomEvent('sizeloaded');

    private readonly apolloProvider = inject(Apollo);
    private readonly apollo: ApolloBase = this.apolloProvider.use('icons');

    getAll() {
        this.getVersion();
        this.getIcons();
        this.getCategories();
        this.getIconStyles();
        this.getSizes();
    }

    getVersion() {
        this.vloading.next(true);
        this.apollo
            .watchQuery({
                query: gql`
                    {
                        version
                    }
                `,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                map((result: any) => {
                    const version = result?.data?.version ?? 'Not loaded';
                    return { result, version, signature: String(version) };
                }),
                distinctUntilChanged(
                    (prev, next) => prev.signature === next.signature,
                ),
                tap(({ result, version }) => {
                    this.version = version;

                    if (result.error) {
                        this.errorMsg = result.errorMsg;
                        this.error.next(result.error);
                    } else {
                        this.errorMsg = '';
                        this.error.next(result.error);
                    }

                    dispatchEvent(this.vloaded);
                    this.vloading.next(false);
                }),
            )
            .subscribe();
    }

    getIcons() {
        this.apollo
            .watchQuery({
                query: gql`
                    {
                        icons {
                            name
                            number
                            title
                            category
                            alternativelabels
                            tags
                            products
                            iconstyle
                            size
                            svg
                        }
                    }
                `,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                map((result: any) => {
                    const data = result?.data;
                    const rawIcons = data?.icons ?? [];
                    const icons = rawIcons.map((icon: any) => new Icon(icon));

                    const signature = rawIcons
                        .map(
                            (i: any) =>
                                `${i?.number ?? ''}|${i?.name ?? ''}|${i?.iconstyle ?? ''}|${i?.size ?? ''}`,
                        )
                        .join(',');

                    return { result, icons, signature };
                }),
                distinctUntilChanged(
                    (prev, next) => prev.signature === next.signature,
                ),
                tap(({ result, icons }) => {
                    this.icons = icons;

                    if (result.error) {
                        this.errorMsg = result.errorMsg;
                        this.error.next(result.error);
                    } else {
                        this.errorMsg = '';
                        this.error.next(result.error);
                    }

                    dispatchEvent(this.loaded);
                    this.loading.next(false);
                }),
            )
            .subscribe();
    }

    getIconStyles() {
        this.apollo
            .watchQuery({
                query: gql`
                    {
                        iconstyles {
                            iconstyle
                        }
                    }
                `,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                map((result: any) => {
                    const data = result?.data;
                    const rawStyles = data?.iconstyles ?? [];
                    const iconstyles = rawStyles.map(
                        (style: any) => new IconStyle(style),
                    );
                    const signature = rawStyles
                        .map((s: any) => s?.iconstyle ?? '')
                        .join(',');
                    return { result, iconstyles, signature };
                }),
                distinctUntilChanged(
                    (prev, next) => prev.signature === next.signature,
                ),
                tap(({ result, iconstyles }) => {
                    this.iconstyles = iconstyles;

                    if (result.error) {
                        this.errorMsg = result.errorMsg;
                        this.error.next(result.error);
                    } else {
                        this.errorMsg = '';
                        this.error.next(result.error);
                    }

                    dispatchEvent(this.sloaded);
                    this.sloading.next(false);
                }),
            )
            .subscribe();
    }

    getCategories() {
        this.apollo
            .watchQuery({
                query: gql`
                    {
                        categories {
                            category
                        }
                    }
                `,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                map((result: any) => {
                    const data = result?.data;
                    const categories = (data?.categories ?? [])
                        .map((category: any) => new Category(category))
                        .sort((a: Category, b: Category) => {
                            if (a.category < b.category) return -1;
                            if (a.category > b.category) return 1;
                            return 0;
                        });

                    return { result, categories };
                }),
                distinctUntilChanged((prev, next) => {
                    if (prev.categories.length !== next.categories.length)
                        return false;
                    for (let i = 0; i < prev.categories.length; i++) {
                        if (
                            prev.categories[i].category !==
                            next.categories[i].category
                        )
                            return false;
                    }
                    return true;
                }),
                tap(({ result, categories }) => {
                    this.categories = categories;

                    if (result.error) {
                        this.errorMsg = result.errorMsg;
                        this.error.next(result.error);
                    } else {
                        this.errorMsg = '';
                        this.error.next(result.error);
                    }

                    dispatchEvent(this.cloaded);
                    this.cloading.next(false);
                }),
            )
            .subscribe();
    }

    getSizes() {
        this.apollo
            .watchQuery({
                query: gql`
                    {
                        icons {
                            size
                        }
                    }
                `,
            })
            .valueChanges.pipe(
                filter((result: any) => !result?.loading),
                map((result: any) => {
                    const data = result?.data;
                    const rawIcons = data?.icons ?? [];

                    const uniqueSizes = [
                        ...new Set(rawIcons.map((item: any) => item?.size)),
                    ].filter((s) => s !== undefined && s !== null);

                    const sizes = uniqueSizes.map(
                        (size: any) => new Size({ size }),
                    );
                    const signature = uniqueSizes.map(String).join(',');

                    return { result, sizes, signature };
                }),
                distinctUntilChanged(
                    (prev, next) => prev.signature === next.signature,
                ),
                tap(({ result, sizes }) => {
                    this.sizes = sizes;

                    if (result.error) {
                        this.errorMsg = result.errorMsg;
                        this.error.next(result.error);
                    } else {
                        this.errorMsg = '';
                        this.error.next(result.error);
                    }

                    dispatchEvent(this.zloaded);
                    this.zloading.next(false);
                }),
            )
            .subscribe();
    }

    getPNGUrl(icon: Icon, theme: string): string {
        let url = `${environment.cdn}/icons/png/${icon.iconstyle}/${icon.size}/${theme}/${icon.category}/${icon.name}.png`;
        return url;
    }

    getSVGUrl(icon: Icon, theme: string): string {
        let url = `${environment.cdn}/icons/svg/${icon.iconstyle}/${icon.size}/${theme}/${icon.category}/${icon.name}.svg`;
        return url;
    }

    getSpriteUrl(icon: Icon, theme: string): string {
        let url = `${environment.cdn}/icons/svg/sprites/uilab-icon-sprite-${icon.iconstyle}-symbols.svg`;
        return url;
    }
}
